// Voice messages (plan v2 §5.3): a recording or text to speech, never changed in place (new words or a new file = a
// new version), approved by a second person. Every message is played before "press 1 for yes", so its words are the
// consent wording: on approval they are registered in the CMP (POST /v1/consent-texts) as a wording version, and an
// IVR node plays only an approved, registered message (N1).
import type { CreateVoiceMessageInput, User, VoiceContentInput, VoiceDecisionInput } from '../../schemas'
import { NodeType } from '../../schemas'
import { refuse } from '../refuse'
import type { State, VoiceMessageRecord } from '../state'
import { apiSwitchedOff, countCampaignCall } from './api-traffic'
import { newId } from './ids'
import { appendEvent } from './log'
import { nowIso } from './sending-window'

export const CONSENT_TEXT_API = 'cmp-consent-text'
/** Text to speech speaks about 2.5 words a second. */
const WORDS_PER_SECOND = 2.5
const IVR_NODE = NodeType.enum['campaign.ivr_call']

export function ttsSeconds(words: string): number {
  return Math.max(3, Math.round(words.trim().split(/\s+/).length / WORDS_PER_SECOND))
}

/** "Consent ask" v2 → ivr-consent-ask-v2: the wording version the CMP keeps for this message. */
export function wordingVersionOf(message: Pick<VoiceMessageRecord, 'name' | 'version'>): string {
  const slug = message.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `ivr-${slug || 'message'}-v${message.version}`
}

/** An IVR node may play it: approved, so its words are a registered consent wording. */
export function playable(message: VoiceMessageRecord): boolean {
  return message.status === 'approved' && !!message.wording_version
}

/** "Name" v2 (live) for every workflow version (not retired) whose IVR node plays this message. */
export function voiceUsedBy(state: State, messageId: string): string[] {
  return state.versions
    .filter((v) => v.status !== 'retired')
    .filter((v) => v.document.nodes.some((n) => n.type === IVR_NODE && n.cfg.message === messageId))
    .map((v) => {
      const name = state.workflows.find((w) => w.id === v.workflow_id)?.name ?? v.workflow_id
      return `"${name}" v${v.version} (${v.status})`
    })
}

function record(
  state: State,
  name: string,
  version: number,
  content: VoiceContentInput,
  maker: User
): VoiceMessageRecord {
  const file =
    content.source === 'recording'
      ? {
          name: content.file.name,
          format: content.file.format,
          size_bytes: content.file.size_bytes,
          seconds: content.file.seconds,
        }
      : null
  const message: VoiceMessageRecord = {
    id: newId(state, 'vm'),
    name,
    version,
    source: content.source,
    language: content.language,
    words: content.words,
    file,
    seconds: file ? file.seconds : ttsSeconds(content.words),
    status: 'waiting_approval',
    made_by: maker.id,
    made_at: nowIso(state),
    decided_by: null,
    decided_at: null,
    note: '',
    wording_version: null,
  }
  state.voiceMessages.unshift(message)
  return message
}

export function createVoiceMessage(
  state: State,
  input: CreateVoiceMessageInput,
  maker: User
): VoiceMessageRecord {
  const { name, ...content } = input
  if (state.voiceMessages.some((m) => m.name.toLowerCase() === name.toLowerCase()))
    refuse('That name is taken: make a new version of that message instead.')
  return record(state, name, 1, content, maker)
}

export function newVoiceVersion(
  state: State,
  from: VoiceMessageRecord,
  input: VoiceContentInput,
  maker: User
): VoiceMessageRecord {
  const family = state.voiceMessages.filter((m) => m.name === from.name)
  const newest = family.reduce((a, b) => (b.version > a.version ? b : a))
  const sameFile = input.source === 'recording' ? input.file.name === newest.file?.name : !newest.file
  if (input.words.trim() === newest.words.trim() && sameFile && input.language === newest.language)
    refuse('Nothing changed: new words or a new file make a new version.')
  return record(state, from.name, newest.version + 1, input, maker)
}

export function decideVoiceMessage(
  state: State,
  message: VoiceMessageRecord,
  input: VoiceDecisionInput,
  decider: User
): void {
  if (message.status !== 'waiting_approval') refuse('Only a message waiting for approval can be decided.')
  if (message.made_by === decider.id) refuse('A second person must approve.')
  if (input.decision === 'approve') {
    if (apiSwitchedOff(state, CONSENT_TEXT_API))
      refuse(
        'The CMP\'s "Register voice wording" API is switched off (Vendors): approve again when it is on.'
      )
    // the words become a consent wording version in the CMP, with the purposes the digit press agrees to
    countCampaignCall(state, [CONSENT_TEXT_API], null)
    message.wording_version = wordingVersionOf(message)
    appendEvent(
      state,
      'out',
      'cmp.consent_text',
      `${message.wording_version} registered (contact on WhatsApp, share with lenders)`
    )
  }
  message.status = input.decision === 'approve' ? 'approved' : 'rejected'
  message.decided_by = decider.id
  message.decided_at = nowIso(state)
  message.note = input.decision === 'reject' ? (input.note ?? '') : ''
}
